/**
 * SchemaDDLViewer — read-only Cypher DDL display for a case's generated
 * schema design.
 *
 * Fetches the latest schema's Cypher via the backend route
 *   GET /case-analysis/cases/{caseId}/schema/cypher-ddl
 * (exposed as `caseAnalysisAPI.getSchemaCypherDDL`). Treats a 404 that
 * persists across a few retries as an empty state — the schema simply hasn't
 * been generated yet — so this component can be mounted unconditionally inside
 * CaseDetail's Schema tab regardless of where the case is in its workflow. The
 * retries matter because a 404 is also what a not-yet-saved schema returns for
 * a few seconds after generation; see the `retry` comment below.
 *
 * Features:
 *   - Syntax-highlighted Cypher (via react-syntax-highlighter + HLJS docco
 *     style, matching IntegrationCodeViewer's existing convention).
 *   - "Copy DDL" button (uses the Clipboard API; falls back to no-op + toast).
 *   - "Download .cypher" button that produces a file named after the schemaId.
 *
 * Returns null when caseId is missing so callers can mount unconditionally.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { Database, Copy, Check, Download, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardBody, CardHeader } from '../ui/Card';
import Button from '../ui/Button';
import { caseAnalysisAPI } from '../../lib/api';

interface SchemaDDLViewerProps {
  caseId: string | null | undefined;
}

interface SchemaDDLResponse {
  schemaId: string;
  cypherDDL: string;
}

// Narrow form of axios/fetch error envelopes for HTTP status inspection
// without leaning on `any`. The two shapes seen in this codebase are
// `error.response.status` (axios) and `error.status` (custom wrappers).
interface HttpErrorLike {
  response?: { status?: number };
  status?: number;
}

function httpStatusOf(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as HttpErrorLike;
  return e.response?.status ?? e.status;
}

const STALE_30_MIN = 1000 * 60 * 30;

export default function SchemaDDLViewer({ caseId }: SchemaDDLViewerProps) {
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error, refetch, isRefetching } = useQuery<SchemaDDLResponse>({
    queryKey: ['case-analysis', 'schema-cypher-ddl', caseId],
    queryFn: () => caseAnalysisAPI.getSchemaCypherDDL(caseId!),
    enabled: !!caseId,
    staleTime: STALE_30_MIN,

    // A 404 here means one of two things, and they need opposite handling:
    //
    //   (a) the schema genuinely has not been generated yet  -> show the CTA
    //   (b) the schema is being saved RIGHT NOW              -> retry
    //
    // Case (b) is common: the user clicks "Show Cypher DDL" as soon as
    // generation finishes, but the save lands a few seconds later. Measured at
    // 5s in one session. Treating 404 as terminal cached that miss for the full
    // 30-minute staleTime, so the panel stayed empty even though the DDL existed.
    //
    // Retry a 404 a few times with backoff — that covers the save window — then
    // stop, so a case with no schema still settles on the empty state.
    retry: (count, err) => {
      const status = httpStatusOf(err);
      if (status === 404) return count < 3;   // ~1s, 2s, 4s
      return count < 1;
    },
    retryDelay: (count) => Math.min(1000 * 2 ** count, 5000),
  });

  if (!caseId) return null;

  if (isLoading) {
    // Skeleton mirrors the loaded layout: header strip with title + action
    // buttons, then a code block with placeholder lines. Mimicking the
    // final shape avoids the layout shift a centered spinner would cause
    // when the DDL lands.
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 animate-pulse">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-neutral-200 rounded" />
              <div className="space-y-1.5">
                <div className="h-4 w-28 bg-neutral-200 rounded" />
                <div className="h-2.5 w-40 bg-neutral-100 rounded" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-7 w-24 bg-neutral-200 rounded" />
              <div className="h-7 w-32 bg-neutral-200 rounded" />
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="rounded-lg border border-neutral-200 overflow-hidden bg-neutral-50 p-4 space-y-2 animate-pulse">
            {[80, 60, 90, 70, 55, 85, 65, 75].map((w, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-2.5 w-6 bg-neutral-200 rounded shrink-0" />
                <div
                  className="h-2.5 bg-neutral-200 rounded"
                  style={{ width: `${w}%` }}
                />
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  // 404 from the backend means schema hasn't been generated yet — surface a
  // friendly CTA pointing the user back to the Analysis wizard.
  if (httpStatusOf(error) === 404) {
    return (
      <Card>
        <CardBody>
          <div className="flex flex-col items-center gap-2 text-neutral-600 py-10 text-center">
            <Database className="w-8 h-8 text-neutral-400" />
            <p className="text-sm font-medium text-neutral-700">
              No schema generated yet
            </p>
            <p className="text-xs text-neutral-500 max-w-md">
              Generate a schema from approved data mappings via the Analysis wizard
              (Step 4) to populate this view.
            </p>
            {/* The retries above cover the usual save window, but a user who
                opens this panel very early can still land here while the save
                is in flight. Offer an on-demand re-check rather than making
                them reload the page. */}
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              <RefreshCw
                className={`w-4 h-4 mr-1.5 ${isRefetching ? 'animate-spin' : ''}`}
              />
              {isRefetching ? 'Checking…' : 'Check again'}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <p className="text-sm flex-1">
          Could not load Cypher DDL.{' '}
          <span className="text-xs">{(error as Error).message}</span>
        </p>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <RefreshCw
            className={`w-4 h-4 mr-1.5 ${isRefetching ? 'animate-spin' : ''}`}
          />
          {isRefetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    );
  }

  if (!data?.cypherDDL) {
    // Schema record exists but the DDL field is empty — rare but possible
    // when an older record was persisted before the DDL generator landed.
    return (
      <Card>
        <CardBody>
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-center text-sm text-neutral-500">
              Schema exists but no Cypher DDL is stored on the record yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              <RefreshCw
                className={`w-4 h-4 mr-1.5 ${isRefetching ? 'animate-spin' : ''}`}
              />
              {isRefetching ? 'Checking…' : 'Check again'}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.cypherDDL);
      setCopied(true);
      toast.success('Cypher DDL copied to clipboard');
      // 2s feedback window before the icon resets — matches the
      // copy-feedback pattern used in IntegrationCodeViewer.
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Copy failed — your browser blocked clipboard access');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([data.cypherDDL], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.schemaId || 'schema'}.cypher`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Line + character counts give the user a quick sense of schema size
  // without having to scroll the syntax-highlighted block.
  const lineCount = data.cypherDDL.split('\n').length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-primary-600" />
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Cypher DDL</h2>
              <p className="text-xs text-neutral-500 mt-0.5">
                Schema {data.schemaId} · {lineCount} line{lineCount === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy DDL
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Download .cypher
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="rounded-lg border border-neutral-200 overflow-hidden">
          <SyntaxHighlighter
            language="cypher"
            style={docco}
            showLineNumbers
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '0.8125rem',
              lineHeight: '1.5',
              background: '#fafafa',
              maxHeight: '600px',
            }}
            lineNumberStyle={{
              color: '#a3a3a3',
              minWidth: '2.5em',
              paddingRight: '1em',
              userSelect: 'none',
            }}
          >
            {data.cypherDDL}
          </SyntaxHighlighter>
        </div>
      </CardBody>
    </Card>
  );
}
